import type { z } from 'zod'

/**
 * Catalog of supported email types. Each entry maps to a distinct Listmonk
 * template (one template per type). To add a new type:
 *   1. Append the literal here.
 *   2. Add a Zod schema for its variables under `./schemas/`.
 *   3. Add a registry entry in `./registry.ts` (TS exhaustiveness will require it).
 *   4. Add a `LISTMONK_TEMPLATE_ID_*` env var across the env / CI / Edge surfaces.
 */
export const EMAIL_TYPES = [
  'tenant-onboarding',
  'platform-invitation',
  'team-invitation',
  'team-invitation-resend',
  'account-activated',
] as const
export type EmailType = (typeof EMAIL_TYPES)[number]

/**
 * A registered email template definition.
 * @template S - Zod schema describing the variables required to render the template.
 */
export interface EmailTemplate<S extends z.ZodTypeAny = z.ZodTypeAny> {
  /** The email type key (matches an entry in EMAIL_TYPES). */
  type: EmailType
  /** Name of the env var holding the Listmonk numeric template id. */
  templateEnvVar: string
  /** Zod schema validating the template variables. */
  variablesSchema: S
  /** Value written to email_events.source_type for correlation. */
  sourceType: string
}

/**
 * Discriminated string union of all error codes emitted by email adapters.
 * Used for logging, metrics, and retry logic.
 */
export type EmailErrorCode =
  | 'auth_error'
  | 'invalid_template'
  /** Send-side: Listmonk reported the subscriber missing (after upsert retry). */
  | 'subscriber_not_found'
  /** Upsert-side: Listmonk rejected subscriber creation (e.g. 4xx validation). */
  | 'subscriber_creation_failed'
  | 'rate_limited'
  | 'upstream_5xx'
  | 'network_error'
  | 'timeout'
  | 'malformed_response'
  | 'bad_request'
  | 'smtp_transient'

/** Result returned by EmailService implementations. Never throws — adapters classify errors. */
export interface EmailSendResult {
  success: boolean
  /** Provider-side or app-generated message id (when available). */
  messageId?: string
  /** True when the caller should retry; false for terminal errors (auth, validation, etc.). */
  retryable?: boolean
  /** Stable error code for logging/metrics. Present only on failure. */
  errorCode?: EmailErrorCode
  /** Human-readable error detail (truncated by adapters to <=500 chars). */
  errorMessage?: string
}
